import clsx from 'clsx';
import { type ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';

import {
  expandableTextCSS,
  expandableTextButtonCSS,
} from './ExpandableText.sty.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
// #region Typings

/**
 * @public
 */
export interface ExpandableTextBaseProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * Text shown for expanding the content.
   * @defaultValue 'Show more'
   */
  expandLabel?: ReactNode;
  /**
   * Text shown for collapsing the content.
   * @defaultValue 'Show less'
   */
  collapseLabel?: ReactNode;
}

/**
 * @public
 */
export interface ExpandableTextUncontrolledProps {
  /**
   * Determines whether or not the content is initially expanded in an uncontrolled scenario.
   * @defaultValue false
   */
  defaultExpanded?: boolean;
  /**
   * Determines whether or not the content is expanded in a controlled scenario.
   */
  expanded?: never;
  /**
   * Callback fired when the expanded attribute changes in a controlled scenario.
   */
  onExpandChange?: never;
}

/**
 * @public
 */
export interface ExpandableTextControlledProps {
  /**
   * Determines whether or not the content is initially expanded in an uncontrolled scenario.
   */
  defaultExpanded?: never;
  /**
   * Determines whether or not the content is expanded in a controlled scenario.
   */
  expanded: boolean;
  /**
   * Callback fired when the expanded attribute changes in a controlled scenario.
   */
  onExpandChange: (expanded: boolean) => void;
}

/**
 * Accepted properties for ExpandableText
 * @public
 */
export type ExpandableTextProps = ExpandableTextBaseProps &
  (ExpandableTextControlledProps | ExpandableTextUncontrolledProps);

/**
 * The `ExpandableText` component provides expand and collapse functionality for inline text.
 * @public
 */
export const ExpandableText = forwardRef<HTMLElement, ExpandableTextProps>(
  (props, forwardedRef) => {
    const {
      children,
      style: consumerStyle,
      className: consumerClassName,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      expandLabel = (
        <FormattedMessage
          description="Shown for expandable text."
          defaultMessage="Show more"
          id="DV3d5lBJda+EHJja"
        />
      ),
      collapseLabel = (
        <FormattedMessage
          description="Shown for collapsible text."
          defaultMessage="Show less"
          id="986buSbL34GVFlBf"
        />
      ),
      defaultExpanded = false,
      expanded,
      onExpandChange,
    } = props;

    const { focusClassName, focusProps, isFocusVisible } = useFocusRing({
      color: 'neutral',
      isMinimal: true,
    });

    const [isExpanded, setExpanded] = useControllableState({
      defaultValue: defaultExpanded,
      value: expanded,
      onChange: onExpandChange,
    });

    const textId = useId();

    // note: need to check for more than just space character
    const beginsWithWhitespace =
      Array.isArray(children) === false &&
      typeof children === 'string' &&
      /^\s/.test(children);

    return (
      <>
        <span
          ref={forwardedRef}
          id={textId}
          role="region"
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          className={clsx(
            consumerClassName,
            expandableTextCSS({ expanded: isExpanded }),
          )}
          style={consumerStyle}
        >
          {isExpanded && beginsWithWhitespace && ' '}
          {isExpanded && children}
        </span>{' '}
        <button
          onClick={() => setExpanded(!isExpanded)}
          aria-controls={textId}
          aria-expanded={isExpanded}
          className={clsx(expandableTextButtonCSS, focusClassName)}
          style={{ textDecoration: isFocusVisible ? 'none' : undefined }}
          {...focusProps}
        >
          {isExpanded ? collapseLabel : expandLabel}
        </button>
      </>
    );
  },
);
